import { AppContext, AppProps } from 'next/app';
import { RecoilRoot } from 'recoil';
import { AppProvider as ShopifyProvider } from '@shopify/polaris';
import { ThemeProvider } from 'styled-components';
import { Provider } from '@shopify/app-bridge-react';
import ClientRouter from '@shopfy/client-router';
import { ErrorBoundary } from 'react-error-boundary';

import defaultTheme from '@styles/theme';
import GlobalStyle from '@styles/global';

import { AppProvider } from '@context/app';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@shopify/polaris/build/esm/styles.css';
import translations from '@shopify/polaris/locales/en.json';
import { initializeState } from '@states';
import ErrorMessage from '@components/errors/message';
import { Routes } from '@routes';
import { handleOnError } from '@utils/errors';
import { Context } from '../types';

interface ShopifyAppProps {
  host: string;
  context: Context;
  path: string;
}

function App({
  Component,
  context,
  host,
  pageProps,
  path,
}: AppProps & ShopifyAppProps) {
  const config = {
    apiKey: SHOPIFY_API_KEY,
    forceRedirect: true,
    host,
  };

  // Assume we're routing to / and need to render the fail catch all
  if (path === Routes.catchAllFail) {
    return (
      <ShopifyProvider i18n={translations}>
        <Component {...pageProps} />
      </ShopifyProvider>
    );
  }

  if (path === Routes.exitIframe) {
    return (
      <Provider config={config}>
        <ClientRouter />
        <ShopifyProvider i18n={translations}>
          <Component {...pageProps} />
        </ShopifyProvider>
      </Provider>
    );
  }

  return (
    <Provider config={config}>
      <ClientRouter />
      <ShopifyProvider i18n={translations}>
        <GlobalStyle />
        <ThemeProvider theme={defaultTheme}>
          <ErrorBoundary
            FallbackComponent={ErrorMessage}
            onError={handleOnError}
          >
            <RecoilRoot initializeState={initializeState(context)}>
              <AppProvider showSaveBar={false} showPageActions={false}>
                <Component {...pageProps} />
              </AppProvider>
            </RecoilRoot>
          </ErrorBoundary>
        </ThemeProvider>
      </ShopifyProvider>
    </Provider>
  );
}

App.getInitialProps = async ({ ctx }: AppContext): Promise<ShopifyAppProps> => {
  return {
    context: ctx.req?.context,
    host: ctx.query.host as string,
    path: ctx.pathname,
  };
};

export default App;
